import React, { MouseEvent, useMemo } from "react";

import { Menu } from "@mui/material";
import MUIMenuItem from "@mui/material/MenuItem";

import Badge from "veneer/Badge";
import Box from "veneer/Box";
import Button from "veneer/Button";
import Checkbox from "veneer/Checkbox";
import { IconButton } from "veneer/IconButton";
import Stack from "veneer/Stack";
import { WorkspaceIcon } from "veneer/SvgIcon";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

export type GroupByOption = {
  id: string;
  label: string;
};

interface GroupByMenuProps {
  anchorPosition: { left: number; top: number } | null;
  grouping: ReadonlyArray<string>;
  open: boolean;
  options: ReadonlyArray<GroupByOption>;
  onClear: () => void;
  onClose: () => void;
  onToggle: (id: string) => void;
}

interface GroupByMenuTriggerProps {
  grouping: ReadonlyArray<string>;
  onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
}

function stopMenuEvent(event: {
  preventDefault: () => void;
  stopPropagation: () => void;
}) {
  event.preventDefault();
  event.stopPropagation();
}

const ButtonWrapper = styled(Box)({
  backgroundColor: Colors.white,
  borderBottom: `1px solid ${Colors.gray200}`,
  minWidth: 244,
  position: "sticky",
  top: 0,
  zIndex: 1,
});

const ListItemsWrapper = styled(Box)({
  flex: "1 1 auto",
  overflow: "auto",
});

const FullWidthMenuItem = styled(MUIMenuItem)({
  width: "100%",
});

export function GroupByMenuTrigger({
  grouping,
  onOpen,
}: GroupByMenuTriggerProps) {
  return (
    <Stack alignItems="center" direction="row" gap={1}>
      <Badge badgeContent={grouping.length} color="lightblue">
        <IconButton
          color="inherit"
          dataTestId="group-by-context-menu-icon"
          icon={<WorkspaceIcon />}
          onClick={onOpen}
          title="Group By"
        />
      </Badge>
    </Stack>
  );
}

export default function GroupByMenu({
  anchorPosition,
  grouping,
  open,
  onClear,
  options,
  onClose,
  onToggle,
}: GroupByMenuProps) {
  const groupedSet = useMemo(() => new Set(grouping), [grouping]);

  const handleToggle = (event: MouseEvent<HTMLLIElement>, id: string) => {
    stopMenuEvent(event);
    onToggle(id);
  };

  const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
    stopMenuEvent(event);
    onClear();
  };

  return (
    <Menu
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      anchorPosition={anchorPosition ?? undefined}
      anchorReference="anchorPosition"
      keepMounted
      MenuListProps={{
        disablePadding: true,
        onClick: (event) => {
          event.stopPropagation();
        },
      }}
      onClose={onClose}
      open={open}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <ButtonWrapper px={2} py={1}>
        <Button
          dataTestId="group-by-clear-all"
          disabled={grouping.length === 0}
          onClick={handleClear}
          variant="text"
        >
          Clear all
        </Button>
      </ButtonWrapper>
      <ListItemsWrapper>
        {options.map((option) => {
          const checked = groupedSet.has(option.id);

          return (
            <FullWidthMenuItem
              data-test-id={`group-by-option-${option.id}`}
              key={option.id}
              onClick={(event) => handleToggle(event, option.id)}
            >
              <Box display="flex" justifyContent="center" minWidth={32} mr={1}>
                <Checkbox checked={checked} />
              </Box>
              <Typography component="span" flex={1}>
                {option.label}
              </Typography>
            </FullWidthMenuItem>
          );
        })}
      </ListItemsWrapper>
    </Menu>
  );
}
